<template>
  <div class="score-config-view">
    <v-container fluid class="pa-4">
      <v-card class="modern-card" elevation="8">
        <v-card-title class="modern-card-title d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left large color="primary">mdi-cog</v-icon>
            <span class="text-h5 ml-2">Configuração da Análise de Score</span>
          </div>
        </v-card-title>

        <v-card-text>
          <!-- Mensagem de feedback -->
          <v-alert
            v-model="showFeedback"
            :type="feedbackType"
            dismissible
            class="mb-4"
            @input="showFeedback = false"
          >
            {{ feedbackMessage }}
          </v-alert>

          <form @submit.prevent="salvarConfiguracoes">
            <!-- Seção: Limites de Processamento -->
            <v-card class="config-section mb-4" elevation="2">
              <v-card-title class="config-section-title">
                <v-icon left>mdi-chart-line</v-icon>
                Limites de Processamento
              </v-card-title>
              <v-card-text>
                <v-text-field
                  v-model.number="config.limiteGb"
                  label="Limite de tamanho (GB) para atualizações Manuais"
                  type="number"
                  step="1"
                  min="1"
                  required
                  outlined
                  dense
                  class="mb-2"
                >
                  <template v-slot:append-outer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Tamanho máximo em GB permitido para hosts que necessitam de atualização manual.</span>
                    </v-tooltip>
                  </template>
                </v-text-field>

                <v-text-field
                  v-model.number="config.scoreLimiteAceito"
                  label="Score limite aceito (Cronograma)"
                  type="number"
                  step="0.1"
                  min="0"
                  required
                  outlined
                  dense
                  class="mb-2"
                >
                  <template v-slot:append-outer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Score máximo para que um host seja considerado <strong>apto</strong> para o Cronograma Diário.</span>
                    </v-tooltip>
                  </template>
                </v-text-field>

                <v-text-field
                  v-model.number="config.scoreLimiteBloqueio"
                  label="Score limite de bloqueio (Atenção/Bloqueio)"
                  type="number"
                  step="0.1"
                  min="0"
                  required
                  outlined
                  dense
                  class="mb-2"
                >
                  <template v-slot:append-outer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Score a partir do qual o host entra em estado de <strong>Atenção</strong> ou é <strong>Bloqueado</strong> do Cronograma.</span>
                    </v-tooltip>
                  </template>
                </v-text-field>

                <v-text-field
                  v-model.number="config.diasLimiteBloqueio"
                  label="Dias limite de bloqueio (Manuais)"
                  type="number"
                  step="1"
                  min="1"
                  required
                  outlined
                  dense
                >
                  <template v-slot:append-outer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Número de dias a partir do qual o host é considerado crítico/manual, independente do score.</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-card-text>
            </v-card>

            <!-- Seção: Pesos da Métrica de Score -->
            <v-card class="config-section mb-4" elevation="2">
              <v-card-title class="config-section-title">
                <v-icon left>mdi-weight-kilogram</v-icon>
                Pesos da Métrica de Score
              </v-card-title>
              <v-card-text>
                <v-alert
                  type="info"
                  outlined
                  class="mb-4"
                >
                  <div class="formula-info">
                    <strong>Fórmula do Score:</strong><br>
                    <code>Peso_Total = (Peso_Dias × Dias_Desatualizados) + Peso_Versões</code>
                  </div>
                </v-alert>

                <v-text-field
                  v-model.number="config.pesoDias"
                  label="Peso de Dias (Multiplicador)"
                  type="number"
                  step="0.1"
                  min="0"
                  required
                  outlined
                  dense
                  class="mb-2"
                >
                  <template v-slot:append-outer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Valor que multiplica o número de dias desde a última atualização.</span>
                    </v-tooltip>
                  </template>
                </v-text-field>

                <v-text-field
                  v-model.number="config.pesoVersaoNumerica"
                  label="Peso de Versão Numérica (Ex: 7.85a para 7.86a)"
                  type="number"
                  step="0.5"
                  min="0"
                  required
                  outlined
                  dense
                  class="mb-2"
                >
                  <template v-slot:append-outer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Peso adicionado para cada mudança na <strong>parte numérica</strong> da versão (Ex: de .23 para .24).</span>
                    </v-tooltip>
                  </template>
                </v-text-field>

                <v-text-field
                  v-model.number="config.pesoVersaoLetra"
                  label="Peso de Versão Letra (Ex: 7.85a para 7.85b)"
                  type="number"
                  step="0.5"
                  min="0"
                  required
                  outlined
                  dense
                >
                  <template v-slot:append-outer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Peso adicionado para cada mudança na <strong>parte alfabética</strong> da versão (Ex: de a para b).</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-card-text>
            </v-card>

            <!-- Seção: Contexto da IA -->
            <v-card class="config-section mb-4" elevation="2">
              <v-card-title class="config-section-title">
                <v-icon left>mdi-robot</v-icon>
                Contexto da IA para Análise de Logs
              </v-card-title>
              <v-card-text>
                <v-alert
                  type="info"
                  outlined
                  class="mb-4"
                >
                  <div class="formula-info">
                    <strong>Configuração do Prompt da IA:</strong><br>
                    Configure o contexto que será usado pela IA para analisar os logs de atualização. 
                    O contexto define as diretrizes do sistema, protocolo de análise e regras de formatação.
                  </div>
                </v-alert>

                <div class="json-editor-wrapper mb-2">
                  <label class="json-editor-label">Contexto da IA (JSON)</label>
                  <div class="json-editor-container">
                    <pre
                      ref="formattedJson"
                      class="json-editor-display"
                      contenteditable="true"
                      spellcheck="false"
                      @input="onJsonEditorInput"
                      @blur="onJsonEditorBlur"
                      v-html="highlightedJSON"
                    ></pre>
                    <textarea
                      ref="hiddenTextarea"
                      v-model="contextoIAString"
                      class="json-editor-hidden"
                      aria-hidden="true"
                    ></textarea>
                  </div>
                  <div v-if="contextoIAErrors.length > 0" class="error-messages">
                    <span class="error-text">{{ contextoIAErrors[0] }}</span>
                  </div>
                  <div class="hint-text">
                    Edite o JSON do contexto da IA. Use o botão 'Formatar JSON' para validar e formatar.
                  </div>
                </div>

                <div class="d-flex justify-end mb-2">
                  <v-btn
                    small
                    color="primary"
                    outlined
                    @click="formatarJSON"
                    :disabled="salvando"
                  >
                    <v-icon left small>mdi-code-json</v-icon>
                    Formatar JSON
                  </v-btn>
                  <v-btn
                    small
                    color="grey"
                    outlined
                    @click="resetarContextoIA"
                    :disabled="salvando"
                    class="ml-2"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    Restaurar Original
                  </v-btn>
                </div>

                <v-alert
                  v-if="contextoIAErrors.length > 0"
                  type="error"
                  outlined
                  dense
                  class="mb-2"
                >
                  {{ contextoIAErrors[0] }}
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- Barra de Ações -->
            <div class="action-bar">
              <v-btn
                type="submit"
                color="success"
                large
                :loading="salvando"
                :disabled="salvando"
              >
                <v-icon left>mdi-content-save</v-icon>
                Salvar Configurações
              </v-btn>
              <v-btn
                type="button"
                color="grey"
                large
                @click="resetarConfiguracoes"
                :disabled="salvando"
                class="ml-2"
              >
                <v-icon left>mdi-refresh</v-icon>
                Cancelar/Resetar
              </v-btn>
            </div>
          </form>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import configuracaoScoreService from '@/services/configuracaoScoreService'

export default {
  name: 'ConfiguracaoScoreView',
  data() {
    return {
      config: {
        limiteGb: 0,
        pesoDias: 0,
        pesoVersaoNumerica: 0,
        pesoVersaoLetra: 0,
        scoreLimiteAceito: 0,
        scoreLimiteBloqueio: 0,
        diasLimiteBloqueio: 0
      },
      initialConfig: {},
      salvando: false,
      showFeedback: false,
      feedbackMessage: '',
      feedbackType: 'success',
      contextoIA: null,
      contextoIAString: '',
      contextoIAOriginal: '',
      contextoIAErrors: [],
      configuracoesCarregadas: false,
      highlightedJSON: '',
      isUpdatingJSON: false
    }
  },
  mounted() {
    this.carregarConfiguracoes()
    this.carregarContextoIA()
  },
  watch: {
    contextoIAString(newVal) {
      if (!this.isUpdatingJSON) {
        this.updateHighlightedJSON()
      }
    }
  },
  methods: {
    async carregarConfiguracoes() {
      try {
        const response = await configuracaoScoreService.buscarTodas()
        
        // O interceptor do axios já retorna response.data, então response já é o objeto
        if (response && response.success) {
          const configuracoes = response.configuracoes || []
          
          // Se não houver configurações, manter valores zerados
          if (configuracoes.length === 0) {
            this.configuracoesCarregadas = false
            this.exibirFeedback('Nenhuma configuração encontrada no banco de dados. Os valores estão zerados.', 'warning')
            // Salvar configuração inicial (zerada)
            this.initialConfig = { ...this.config }
            return
          }
          
          // Mapear configurações para o objeto config
          const configMap = {
            'DATABASE_SIZE_LIMIT_GB': 'limiteGb',
            'PESO_DIAS': 'pesoDias',
            'PESO_VERSAO_NUMERICA': 'pesoVersaoNumerica',
            'PESO_VERSAO_LETRA': 'pesoVersaoLetra',
            'SCORE_LIMITE_ACEITO': 'scoreLimiteAceito',
            'SCORE_LIMITE_BLOQUEIO': 'scoreLimiteBloqueio',
            'DIAS_LIMITE_BLOQUEIO': 'diasLimiteBloqueio'
          }
          
          // Resetar valores antes de carregar
          Object.keys(this.config).forEach(key => {
            this.config[key] = 0
          })
          
          configuracoes.forEach(config => {
            const key = configMap[config.chave]
            if (key && config.valor !== null && config.valor !== undefined) {
              this.config[key] = parseFloat(config.valor) || 0
            }
          })
          
          // Salvar configuração inicial
          this.initialConfig = { ...this.config }
          this.configuracoesCarregadas = true
        } else {
          throw new Error(response?.message || 'Erro ao carregar configurações')
        }
      } catch (error) {
        console.error('Erro ao carregar configurações:', error)
        this.configuracoesCarregadas = false
        // Manter valores zerados e informar o usuário
        this.exibirFeedback(
          'Não foi possível carregar as configurações do banco de dados. Os valores estão zerados. Por favor, configure os valores manualmente.',
          'error'
        )
        // Salvar configuração inicial (zerada)
        this.initialConfig = { ...this.config }
      }
    },
    
    async salvarConfiguracoes() {
      // Validar formulário
      if (!this.validarFormulario()) {
        return
      }

      // Validar contexto da IA se foi modificado
      if (this.contextoIAString !== this.contextoIAOriginal) {
        if (!this.validarContextoIA()) {
          return
        }
      }
      
      this.salvando = true
      
      try {
        // Preparar array de configurações para enviar
        const configuracoes = [
          { chave: 'DATABASE_SIZE_LIMIT_GB', valor: this.config.limiteGb },
          { chave: 'PESO_DIAS', valor: this.config.pesoDias },
          { chave: 'PESO_VERSAO_NUMERICA', valor: this.config.pesoVersaoNumerica },
          { chave: 'PESO_VERSAO_LETRA', valor: this.config.pesoVersaoLetra },
          { chave: 'SCORE_LIMITE_ACEITO', valor: this.config.scoreLimiteAceito },
          { chave: 'SCORE_LIMITE_BLOQUEIO', valor: this.config.scoreLimiteBloqueio },
          { chave: 'DIAS_LIMITE_BLOQUEIO', valor: this.config.diasLimiteBloqueio }
        ]
        
        // Salvar configurações de score
        const response = await configuracaoScoreService.atualizar(configuracoes)
        
        // Salvar contexto da IA se foi modificado
        if (this.contextoIAString !== this.contextoIAOriginal) {
          try {
            const contextoParsed = JSON.parse(this.contextoIAString)
            const responseContexto = await configuracaoScoreService.atualizarContextoIA(contextoParsed)
            
            if (responseContexto && responseContexto.success) {
              this.contextoIAOriginal = this.contextoIAString
              this.contextoIA = contextoParsed
            }
          } catch (error) {
            console.error('Erro ao salvar contexto da IA:', error)
            throw new Error('Erro ao salvar contexto da IA: ' + (error.message || 'JSON inválido'))
          }
        }
        
        // O interceptor do axios já retorna response.data, então response já é o objeto
        if (response && response.success) {
          this.initialConfig = { ...this.config }
          this.exibirFeedback('Configurações salvas com sucesso!', 'success')
        } else {
          throw new Error(response?.message || 'Erro ao salvar configurações')
        }
      } catch (error) {
        console.error('Erro ao salvar configurações:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar configurações'
        this.exibirFeedback(errorMessage, 'error')
      } finally {
        this.salvando = false
      }
    },
    
    resetarConfiguracoes() {
      this.config = { ...this.initialConfig }
      this.contextoIAString = this.contextoIAOriginal
      this.contextoIAErrors = []
      this.exibirFeedback('Alterações canceladas. Configurações restauradas.', 'info')
    },
    
    validarFormulario() {
      // Validações básicas
      if (this.config.limiteGb < 1) {
        this.exibirFeedback('Limite de GB deve ser maior que 0', 'error')
        return false
      }
      
      if (this.config.pesoDias < 0) {
        this.exibirFeedback('Peso de Dias deve ser maior ou igual a 0', 'error')
        return false
      }
      
      if (this.config.pesoVersaoNumerica < 0) {
        this.exibirFeedback('Peso de Versão Numérica deve ser maior ou igual a 0', 'error')
        return false
      }
      
      if (this.config.pesoVersaoLetra < 0) {
        this.exibirFeedback('Peso de Versão Letra deve ser maior ou igual a 0', 'error')
        return false
      }
      
      if (this.config.scoreLimiteAceito < 0) {
        this.exibirFeedback('Score Limite Aceito deve ser maior ou igual a 0', 'error')
        return false
      }
      
      if (this.config.scoreLimiteBloqueio < 0) {
        this.exibirFeedback('Score Limite Bloqueio deve ser maior ou igual a 0', 'error')
        return false
      }
      
      if (this.config.diasLimiteBloqueio < 1) {
        this.exibirFeedback('Dias Limite Bloqueio deve ser maior que 0', 'error')
        return false
      }
      
      return true
    },
    
    exibirFeedback(message, type = 'success') {
      this.feedbackMessage = message
      this.feedbackType = type
      this.showFeedback = true
      
      // Auto-ocultar após 5 segundos
      setTimeout(() => {
        this.showFeedback = false
      }, 5000)
    },
    
    async carregarContextoIA() {
      try {
        const response = await configuracaoScoreService.buscarContextoIA()
        
        if (response && response.success && response.contexto) {
          this.contextoIA = response.contexto
          this.contextoIAString = JSON.stringify(response.contexto, null, 2)
          this.contextoIAOriginal = this.contextoIAString
          this.contextoIAErrors = []
          this.updateHighlightedJSON()
        } else {
          // Se não houver contexto, deixar vazio e informar o usuário
          this.contextoIA = null
          this.contextoIAString = ''
          this.contextoIAOriginal = ''
          this.contextoIAErrors = []
          this.highlightedJSON = ''
          this.exibirFeedback('Contexto da IA não encontrado no banco de dados. O campo está vazio. Por favor, configure o contexto.', 'warning')
        }
      } catch (error) {
        console.error('Erro ao carregar contexto da IA:', error)
        // Em caso de erro, deixar vazio e informar o usuário
        this.contextoIA = null
        this.contextoIAString = ''
        this.contextoIAOriginal = ''
        this.contextoIAErrors = []
        this.highlightedJSON = ''
        this.exibirFeedback(
          'Não foi possível carregar o contexto da IA do banco de dados. O campo está vazio. Por favor, configure o contexto manualmente.',
          'error'
        )
      }
    },
    
    highlightJSON(jsonString) {
      if (!jsonString || jsonString.trim() === '') {
        return ''
      }

      // Remove qualquer tag HTML (limpando o texto editado)
      let cleanJson = jsonString.replace(/<[^>]*>/g, '')
      
      try {
        // Tenta formatar o JSON para garantir indentação correta
        cleanJson = JSON.stringify(JSON.parse(cleanJson), null, 2)
      } catch (e) {
        // Se falhar (JSON inválido), retorna o texto bruto sem highlight
        return this.escapeHtml(cleanJson)
      }

      let html = this.escapeHtml(cleanJson)
      
      // Simplificado: Apenas keys em AZUL e números da key "priority" em VERDE
      
      // 1. Keys/Chaves (nomes dos campos) - Azul (#9CDCFE)
      // Captura TODAS as chaves em qualquer nível
      const keyRegex = /(\s*)(".*?")(\s*:\s*)/g
      html = html.replace(keyRegex, (match, indent, key, colon) => {
        const keyContent = key.substring(1, key.length - 1)
        return `${indent}<span class="json-punctuation" style="color: #e0e0e0;">"</span><span class="json-key" style="color: #9CDCFE; font-weight: 500;">${keyContent}</span><span class="json-punctuation" style="color: #e0e0e0;">"</span><span class="json-punctuation" style="color: #e0e0e0;">${colon}</span>`
      })
      
      // 2. Números da key "priority" - Verde (#B5CEA8)
      // Procura por "priority" seguido de : e um número
      // Processar de trás para frente para evitar problemas com índices
      const priorityMatches = []
      let priorityMatch
      const priorityRegex = /("priority"\s*:\s*)(\d+)/g
      while ((priorityMatch = priorityRegex.exec(html)) !== null) {
        const beforeMatch = html.substring(0, priorityMatch.index)
        const lastSpan = beforeMatch.lastIndexOf('<span')
        const lastSpanClose = beforeMatch.lastIndexOf('</span>')
        // Só processar se não estiver dentro de um span
        if (lastSpan <= lastSpanClose) {
          priorityMatches.push({
            keyPart: priorityMatch[1],
            number: priorityMatch[2],
            index: priorityMatch.index,
            keyPartLength: priorityMatch[1].length,
            numberLength: priorityMatch[2].length
          })
        }
      }
      // Processar de trás para frente
      for (let i = priorityMatches.length - 1; i >= 0; i--) {
        const pm = priorityMatches[i]
        const fullMatchLength = pm.keyPartLength + pm.numberLength
        html = html.substring(0, pm.index) + 
               `${pm.keyPart}<span class="json-number" style="color: #B5CEA8;">${pm.number}</span>` + 
               html.substring(pm.index + fullMatchLength)
      }
      
      return html
    },

    escapeHtml(text) {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },

    updateHighlightedJSON() {
      this.highlightedJSON = this.highlightJSON(this.contextoIAString)
    },

    onJsonEditorInput(event) {
      const text = event.target.innerText || event.target.textContent
      this.isUpdatingJSON = true
      this.contextoIAString = text
      this.$nextTick(() => {
        this.isUpdatingJSON = false
        this.updateHighlightedJSON()
        // Restaurar cursor após atualização
        this.$nextTick(() => {
          this.restoreCursor(event.target)
        })
      })
    },

    onJsonEditorBlur(event) {
      // Sincronizar o texto do editor com o modelo
      const text = event.target.innerText || event.target.textContent
      this.isUpdatingJSON = true
      this.contextoIAString = text
      this.$nextTick(() => {
        this.isUpdatingJSON = false
        this.updateHighlightedJSON()
      })
    },

    restoreCursor(element) {
      // Tentar manter a posição do cursor (simplificado)
      try {
        const range = document.createRange()
        const sel = window.getSelection()
        range.selectNodeContents(element)
        range.collapse(false)
        sel.removeAllRanges()
        sel.addRange(range)
      } catch (e) {
        // Ignorar erros de seleção
      }
    },

    formatarJSON() {
      try {
        const parsed = JSON.parse(this.contextoIAString)
        this.isUpdatingJSON = true
        this.contextoIAString = JSON.stringify(parsed, null, 2)
        this.contextoIAErrors = []
        this.$nextTick(() => {
          this.isUpdatingJSON = false
          this.updateHighlightedJSON()
          this.exibirFeedback('JSON formatado com sucesso!', 'success')
        })
      } catch (error) {
        this.contextoIAErrors = ['JSON inválido: ' + error.message]
        this.exibirFeedback('Erro ao formatar JSON: ' + error.message, 'error')
        this.updateHighlightedJSON()
      }
    },
    
    resetarContextoIA() {
      this.contextoIAString = this.contextoIAOriginal
      this.contextoIAErrors = []
      this.updateHighlightedJSON()
      this.exibirFeedback('Contexto da IA restaurado para o valor original.', 'info')
    },
    
    validarContextoIA() {
      try {
        const parsed = JSON.parse(this.contextoIAString)
        
        // Validações básicas
        if (typeof parsed !== 'object' || parsed === null) {
          this.contextoIAErrors = ['O contexto deve ser um objeto JSON']
          return false
        }
        
        // Validar estrutura esperada
        if (parsed.analysis_protocol && !Array.isArray(parsed.analysis_protocol)) {
          this.contextoIAErrors = ['analysis_protocol deve ser um array']
          return false
        }
        
        if (parsed.output_formatting_rules && !Array.isArray(parsed.output_formatting_rules)) {
          this.contextoIAErrors = ['output_formatting_rules deve ser um array']
          return false
        }
        
        this.contextoIAErrors = []
        return true
      } catch (error) {
        this.contextoIAErrors = ['JSON inválido: ' + error.message]
        return false
      }
    }
  }
}
</script>

<style scoped>
.score-config-view {
  min-height: 100vh;
  background-color: var(--v-background-base);
}

.config-section {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
}

.config-section-title {
  border-bottom: 1px dashed rgba(0, 0, 0, 0.12);
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.formula-info {
  font-size: 0.95rem;
  line-height: 1.6;
}

.formula-info code {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

.action-bar {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid rgba(0, 0, 0, 0.12);
  text-align: right;
}

.modern-card {
  border-radius: 12px;
}

.modern-card-title {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

/* Estilos para o editor de JSON com syntax highlighting */
.json-editor-wrapper {
  position: relative;
}

.json-editor-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
}

.theme--dark .json-editor-label {
  color: rgba(255, 255, 255, 0.7);
}

.json-editor-container {
  position: relative;
  font-family: 'Fira Code', 'Courier New', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
  background-color: #1e1e1e;
}

.theme--light .json-editor-container {
  border-color: rgba(0, 0, 0, 0.12);
  background-color: #1e1e1e;
}

.theme--dark .json-editor-container {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: #1e1e1e;
}

.json-editor-display {
  min-height: 350px;
  max-height: 600px;
  padding: 15px;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
  overflow-y: auto;
  outline: none;
  cursor: text;
  color: #e0e0e0 !important;
  background-color: transparent !important;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

/* Garantir que elementos filhos herdem cor apenas se não tiverem classe específica */
.json-editor-display > * {
  color: inherit;
}

.json-editor-display:focus {
  box-shadow: inset 0 0 0 2px rgba(25, 118, 210, 0.3);
}

.json-editor-hidden {
  position: absolute;
  top: -9999px;
  left: -9999px;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

/* Syntax Highlighting Colors - VS Code like */
/* Usar alta especificidade para sobrescrever qualquer estilo do Vuetify */
.json-editor-container .json-editor-display .json-key,
.json-editor-display .json-key,
pre.json-editor-display .json-key,
.json-key {
  color: #9CDCFE !important; /* Azul Claro - Chaves/Nomes de campo */
  font-weight: 500 !important;
}

.json-editor-container .json-editor-display .json-string,
.json-editor-display .json-string,
pre.json-editor-display .json-string,
.json-string {
  color: #CE845E !important; /* Laranja/Cobre - Strings/Valores */
}

.json-editor-container .json-editor-display .json-number,
.json-editor-display .json-number,
pre.json-editor-display .json-number,
.json-number {
  color: #B5CEA8 !important; /* Verde Pastel - Números */
}

.json-editor-container .json-editor-display .json-punctuation,
.json-editor-display .json-punctuation,
pre.json-editor-display .json-punctuation,
.json-punctuation {
  color: #e0e0e0 !important; /* Branco - Vírgulas/Pontos/Aspas */
}

.json-editor-container .json-editor-display .json-bracket,
.json-editor-display .json-bracket,
pre.json-editor-display .json-bracket,
.json-bracket {
  color: #9CDCFE !important; /* Azul Claro - Chaves/Colchetes */
  font-weight: 500 !important;
}

/* Garantir que spans dentro do editor mantenham suas cores específicas */
.json-editor-display span.json-key {
  color: #9CDCFE !important;
  font-weight: 500 !important;
}

.json-editor-display span.json-string {
  color: #CE845E !important;
}

.json-editor-display span.json-number {
  color: #B5CEA8 !important;
}

.json-editor-display span.json-punctuation {
  color: #e0e0e0 !important;
}

.json-editor-display span.json-bracket {
  color: #9CDCFE !important;
  font-weight: 500 !important;
}

/* Sobrescrever qualquer estilo do Vuetify */
.theme--light .json-editor-display .json-key,
.theme--dark .json-editor-display .json-key {
  color: #9CDCFE !important;
}

.theme--light .json-editor-display .json-string,
.theme--dark .json-editor-display .json-string {
  color: #CE845E !important;
}

.theme--light .json-editor-display .json-number,
.theme--dark .json-editor-display .json-number {
  color: #B5CEA8 !important;
}

.theme--light .json-editor-display .json-punctuation,
.theme--dark .json-editor-display .json-punctuation {
  color: #e0e0e0 !important;
}

.theme--light .json-editor-display .json-bracket,
.theme--dark .json-editor-display .json-bracket {
  color: #9CDCFE !important;
}

.error-messages {
  margin-top: 4px;
  font-size: 12px;
}

.error-text {
  color: #f44336;
}

.hint-text {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
}

.theme--dark .hint-text {
  color: rgba(255, 255, 255, 0.6);
}
</style>

